/**
 * garabat-referral.js
 * ---------------------------------------------------------------------
 * Orquestador de navegador del cliente de referral del theme. Construye las
 * dependencias reales del entorno (fetch, crypto, storage, cookies, location)
 * y delega TODA la lógica en `GarabatReferralCommon.createClient(deps)` (ver
 * garabat-referral-common.js), que se carga antes que este script.
 *
 * Responsabilidades exclusivas de este archivo (glue de DOM):
 *   - Leer window.GarabatReferralConfig y construir `deps`.
 *   - Cargar el loader global de Viral Loops (widgets v2) con el
 *     viralLoopsCampaignId servido por el backend (nunca el public token).
 *   - Re-aserción de note attributes justo antes de ir a checkout.
 *
 * Modo de atribución:
 *   - `disabled`  → kill switch: no valida ni escribe atributos firmados.
 *   - `signed_v1` → flujo firmado (clientNonce → validate → note attrs).
 *   - `auto`/legacy → comportamiento legacy equivalente al histórico.
 *
 * Nunca incluye VIRAL_LOOPS_API_TOKEN ni SHOPIFY_ADMIN_ACCESS_TOKEN: son
 * secretos de servidor y jamás tocan el navegador. El token de atribución es
 * opaco y solo viaja como note attribute; nunca en URLs ni consola.
 * ---------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CONFIG = window.GarabatReferralConfig || {};
  var Common = window.GarabatReferralCommon;
  if (!Common || typeof Common.createClient !== 'function') return;

  var VIRAL_LOOPS_LOADER_ID = 'viral-loops-loader';
  var VIRAL_LOOPS_LOADER_SRC = 'https://app.viral-loops.com/widgetsV2/core/loader.js';

  function makeUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      return Common.fallbackUuidFrom(function (arr) { window.crypto.getRandomValues(arr); });
    }
    throw new Error('secure_context_required'); // sin crypto seguro no se genera nonce
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  /** Carga el loader global de Viral Loops una sola vez (sin duplicar <script>). */
  function loadViralLoopsSnippet(viralLoopsId) {
    if (!viralLoopsId) return;
    var existing = document.getElementById(VIRAL_LOOPS_LOADER_ID);
    if (existing) {
      existing.setAttribute('data-campaign-id', viralLoopsId);
      return;
    }
    var script = document.createElement('script');
    script.id = VIRAL_LOOPS_LOADER_ID;
    script.setAttribute('data-campaign-id', viralLoopsId);
    script.src = VIRAL_LOOPS_LOADER_SRC;
    script.async = true;
    document.head.appendChild(script);
  }

  // Exclusión mutua cross-tab de las mutaciones del carrito vía Web Locks.
  // Fallback documentado (navegadores sin `navigator.locks`): el módulo común
  // degrada a generación compartida + reconciliación posterior, que NO ofrece
  // exclusión atómica entre pestañas (solo serialización intra-pestaña).
  var cartLocks =
    (window.navigator && window.navigator.locks && typeof window.navigator.locks.request === 'function')
      ? { request: function (name, cb) { return window.navigator.locks.request(name, cb); } }
      : null;

  var deps = {
    fetch: window.fetch ? window.fetch.bind(window) : null,
    now: function () { return Date.now(); },
    uuid: makeUuid,
    localStorage: safeStorage(function () { return window.localStorage; }),
    sessionStorage: safeStorage(function () { return window.sessionStorage; }),
    getCookie: getCookie,
    setRawCookie: function (raw) { document.cookie = raw; },
    location: { href: window.location.href, search: window.location.search },
    config: CONFIG,
    locks: cartLocks,
    onCampaignConfig: function (serverConfig) {
      loadViralLoopsSnippet(serverConfig && serverConfig.viralLoopsCampaignId);
    },
  };

  // Acceder a window.localStorage puede lanzar en algunos navegadores/modo
  // privado incluso al leer la propiedad: lo envolvemos.
  function safeStorage(getter) {
    try { return getter() || null; } catch (_e) { return null; }
  }

  var client = Common.createClient(deps);

  function init() {
    // La lógica (captura de ref, modo, legacy/signed) vive en el módulo común.
    // No await bloqueante: la compra nunca se bloquea por esto.
    try { client.run(); } catch (_e) {}

    // Re-aserción de atributos antes de ir a checkout (algunos temas reinician
    // el cart form entre secciones). Best-effort, sin bloquear la navegación.
    document.addEventListener('submit', function (evt) {
      var form = evt.target;
      if (form && form.action && /\/cart/.test(form.action)) {
        try { client.reassertCart(); } catch (_e) {}
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
